import { LABEL, STAGES } from "./constants";
import {
  EngagementMilestone,
  MilestoneKey,
  SankeyData,
  SankeyLink,
  StageKey,
} from "./models";

export const isDisc = (s: string): boolean =>
  s.toLowerCase() === "discontinued";
export const isComp = (s: string): boolean => s.toLowerCase() === "complete";
export const sum = (nums: number[]): number => nums.reduce((a, b) => a + b, 0);

export const toSankeyData = (
  rows: EngagementMilestone[],
  opts: {
    excludeCorporateIssue?: boolean;
    nodeColors?: Record<string, string>;
  } = {}
): SankeyData => {
  const counts: Record<MilestoneKey, Record<string, number>> = {
    corporate_issue: {},
    finished: {},
    m1_status: {},
    m2_status: {},
    m3_status: {},
    m4_status: {},
  };

  for (const r of rows) {
    if (opts.excludeCorporateIssue && r.current_milestone === "corporate_issue")
      continue;
    const m = r.current_milestone;
    const s = r.current_status;
    counts[m][s] = (counts[m][s] ?? 0) + r.record_count;
  }

  const sumMilestone = (m: MilestoneKey): number =>
    sum(Object.values(counts[m]));
  const corporateIssueTotal = sumMilestone("corporate_issue");
  const completedTotal = sumMilestone("finished");

  const discontinuedTotal = STAGES.reduce((acc, m) => {
    const add = Object.entries(counts[m]).reduce(
      (a, [status, n]) => a + (isDisc(status) ? n : 0),
      0
    );
    return acc + add;
  }, 0);

  const stageNonDiscTotals: Record<StageKey, number> = {
    m1_status: 0,
    m2_status: 0,
    m3_status: 0,
    m4_status: 0,
  };
  for (const m of STAGES) {
    const t = Object.entries(counts[m]).reduce((acc, [status, n]) => {
      if (isDisc(status) || isComp(status)) return acc;
      return acc + n;
    }, 0);
    stageNonDiscTotals[m] = t;
  }

  const totalToM1 =
    completedTotal + STAGES.reduce((a, m) => a + stageNonDiscTotals[m], 0);

  const nodesSet = new Set<string>();
  const links: SankeyLink[] = [];

  const nodeColorsResolved: Record<string, string> = {};

  const link = (
    source: string,
    target: string,
    value: number,
    color?: string
  ): void => {
    if (value > 0) {
      links.push({ source, target, value });
      nodesSet.add(source);
      nodesSet.add(target);
      if (color) nodeColorsResolved[target] = color;
    }
  };

  link("Engagements", LABEL.corporate_issue, corporateIssueTotal);
  link("Engagements", "Discontinued", discontinuedTotal);
  link("Engagements", LABEL.m1_status, totalToM1);

  for (const m of STAGES) {
    const stageLabel = LABEL[m];
    for (const [status, n] of Object.entries(counts[m])) {
      if (n > 0 && !isDisc(status) && !isComp(status)) {
        const stageColor = opts.nodeColors?.[stageLabel];
        link(stageLabel, `${stageLabel} • ${status}`, n, stageColor);
      }
    }
  }

  const forwardFrom = (from: StageKey): number => {
    const i = STAGES.indexOf(from);
    const laterNonDisc = STAGES.slice(i + 1).reduce(
      (a, m) => a + stageNonDiscTotals[m],
      0
    );
    return laterNonDisc + completedTotal;
  };
  link(LABEL.m1_status, LABEL.m2_status, forwardFrom("m1_status"));
  link(LABEL.m2_status, LABEL.m3_status, forwardFrom("m2_status"));
  link(LABEL.m3_status, LABEL.m4_status, forwardFrom("m3_status"));
  link(LABEL.m4_status, LABEL.finished, completedTotal);

  const nodes = Array.from(nodesSet).map((id) => ({
    id,
    color: opts.nodeColors?.[id] ?? nodeColorsResolved[id] ?? "#ccc",
  }));

  return { nodes, links };
};
